import React, { useEffect, useRef, useState } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * ICD-10 diagnosis autocomplete. Backed by CMS 2026 dataset via /api/icd10/search.
 *
 * Props:
 *   value:       string — current diagnosis text (free-form, comma-separated codes ok)
 *   onChange:    (newValue) => void
 *   placeholder: string (optional)
 *   testid:      string (optional)
 *   single:      bool — if true, replaces value on select; if false (default), appends.
 */
export default function Icd10Autocomplete({
  value = "",
  onChange,
  placeholder = "Search ICD-10 code or keyword (e.g. I10, hypertension)",
  testid = "icd10-autocomplete",
  single = false,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const cancelRef = useRef({ cancelled: false });

  // Search is triggered directly from the input's onChange — not a useEffect on query.
  // This satisfies the no-set-state-in-effect lint rule while keeping debounce behavior.
  const runSearch = (q) => {
    if (!q || q.length < 2) {
      setResults([]); setOpen(false); setLoading(false);
      return;
    }
    cancelRef.current.cancelled = true;       // cancel any in-flight prior request
    cancelRef.current = { cancelled: false };
    const myCancel = cancelRef.current;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/icd10/search", { params: { q, limit: 12 } });
        if (myCancel.cancelled) return;
        setResults(data || []); setOpen(true); setHighlightIdx(0);
      } catch {
        if (!myCancel.cancelled) setResults([]);
      } finally {
        if (!myCancel.cancelled) setLoading(false);
      }
    }, 180);
  };

  // Cleanup on unmount
  useEffect(() => () => { cancelRef.current.cancelled = true; clearTimeout(debounceRef.current); }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (item) => {
    if (single) {
      onChange?.(`${item.code} — ${item.description}`);
    } else {
      const entry = `${item.code} — ${item.description}`;
      const next = value ? `${value.trim().replace(/,\s*$/, "")}, ${entry}` : entry;
      onChange?.(next);
    }
    setQuery(""); setOpen(false); setResults([]);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlightIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlightIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && results[highlightIdx]) { e.preventDefault(); pick(results[highlightIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapRef} data-testid={testid}>
      {!single && value && (
        <div className="mb-1 text-[11px] text-brand-muted leading-snug" data-testid={`${testid}-current`}>
          <span className="font-semibold uppercase tracking-wider text-[9px]">Selected:</span>{" "}
          {value}{" "}
          <button onClick={() => onChange?.("")} type="button" className="text-red-600 underline hover:no-underline text-[10px]" data-testid={`${testid}-clear`}>clear</button>
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); runSearch(e.target.value); }}
          onKeyDown={onKey}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="input pl-9"
          style={{ minHeight: 44, fontSize: 16 }}
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-brand-muted" />}
        {!loading && query && (
          <button type="button" onClick={() => { setQuery(""); setResults([]); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-text"
            data-testid={`${testid}-x`}>
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-white border border-brand rounded-md shadow-lg"
             data-testid={`${testid}-dropdown`}>
          {results.map((r, i) => (
            <button
              type="button"
              key={r.code}
              onClick={() => pick(r)}
              onMouseEnter={() => setHighlightIdx(i)}
              className={`w-full text-left px-3 py-2 hover:bg-brand-bg text-sm border-b border-slate-100 last:border-b-0 ${i === highlightIdx ? "bg-brand-bg" : ""}`}
              data-testid={`${testid}-opt-${r.code}`}
            >
              <span className="font-mono font-bold text-brand-primary mr-2 text-xs">{r.code}</span>
              <span className="text-brand-text">{r.description}</span>
            </button>
          ))}
        </div>
      )}
      {open && !loading && query.length >= 2 && results.length === 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 p-3 bg-white border border-brand rounded-md shadow-lg text-xs text-brand-muted" data-testid={`${testid}-empty`}>
          No ICD-10 matches for &quot;{query}&quot;. Try a code prefix (e.g. <b>I10</b>) or keyword (e.g. <b>hypertension</b>).
        </div>
      )}
    </div>
  );
}
